import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { css, cx, useTheme } from '@fable/theme'
import { Club } from '@fable/types'
import {
  ClubDetailSuggestions,
  ClubDetailColumns,
} from '../components/club_detail'
import {
  useInviteId,
  useLoadReferral,
  useTypedSelector,
  useVertExpEnabled,
} from 'hooks'
import { clubAuthKey } from 'state'
import { useClub, useClubBookPromo, useClubMachineState } from '../hooks'
import { clubRoute } from '../routes'
import { clubAnchors, clubAnchorsLegacy } from '../constants'
import { PageDetailAnchorNav } from 'components/page_detail'
import { PageDetailBanner } from 'components/page_detail/PageDetailBanner'
import ClubDetailPrivateClubBlock from '../components/club_detail/ClubDetailPrivateClubBlock'
import { InviteUserCard } from 'components/invite_user_card/InviteUserCard'

const ClubDetailPage = () => {
  const { search } = useLocation()
  const inviteParam = new URLSearchParams(search).get('invite')
  const { colors, mediaQueries } = useTheme()
  const club = useClub()
  const inviteId = useInviteId()
  const { isVerifying, isAuthenticated } = useTypedSelector(({ auth }) => auth)
  const { profile } = useTypedSelector(({ user }) => user)
  const { machine, state } = useClubMachineState()
  const clubUrl = clubRoute({ club })
  const shareUrl = `${window.location.host}${clubUrl.pathname}${clubUrl.search}`
  const promo = useClubBookPromo({ ctx: state.context })
  const isPrivate = club?.access_type === 'private'
  const shouldBlock = isPrivate && !state.context.inviteId && !inviteParam
  const vertExpEnabled = useVertExpEnabled()
  const { referralUser, enabled: referralEnabled } = useLoadReferral()

  /** @note enable post auth behaviour */
  useEffect(() => {
    if (isVerifying) return

    const key = localStorage.getItem(clubAuthKey)

    if (!!profile.data && !promo.active) {
      localStorage.removeItem(clubAuthKey)
      promo.clearPromoKey()
      return
    }
    if (!!key && isAuthenticated) {
      machine.send({ type: 'RESET', data: { postAuth: true } })
    }
  }, [isVerifying, isAuthenticated, machine, profile.data, promo])

  /** @note send initial data to the club machine */
  useEffect(() => {
    if (isVerifying) return

    const data = {
      user: !isEmpty(profile.data) ? profile.data : null,
      club: club as Club,
      inviteId,
    }

    machine.send({ type: 'VERIFY', data })
  }, [machine, club, profile.data, inviteId, isVerifying])

  const pageAnchors = vertExpEnabled ? clubAnchors : clubAnchorsLegacy

  return (
    <>
      {shouldBlock ? (
        <ClubDetailPrivateClubBlock />
      ) : (
        <div
          className={cx(
            css`
              padding-bottom: 100px;
            `,
            !isAuthenticated && referralEnabled
              ? css`
                  padding-top: 100px;
                  ${mediaQueries.mobileL} {
                    padding-top: 20px;
                  }
                `
              : ''
          )}
        >
          {promo.active && (
            <PageDetailBanner
              icon="price-tag"
              backgroundColor={colors.uiGreenUltraLight}
              text="Limited time only: Get $5 off your club's ebook when you join your first book club!"
              persist
            />
          )}
          {club?.is_dormant && (
            <>
              <PageDetailBanner text="Heads up! This club hasn’t had much activity lately." />
              <ClubDetailSuggestions
                className={css`
                  margin-top: 40px;
                  ${mediaQueries.tablet} {
                    margin-bottom: 88px;
                  }
                `}
              />
            </>
          )}
          <PageDetailAnchorNav
            anchors={pageAnchors}
            shareOptions={{ url: shareUrl, category: 'club' }}
          />
          <ClubDetailColumns />
          {(!club?.is_dormant || club.current_book?.type === 'book') && (
            <ClubDetailSuggestions
              className={css`
                margin-top: 40px;
                padding-top: 40px;
                border-top: 1px solid ${colors.whiteSmoke};
              `}
            />
          )}
          {!isAuthenticated && !!referralUser && (
            <InviteUserCard
              className={css`
                padding-top: 72px !important;
              `}
              user={referralUser}
            />
          )}
        </div>
      )}
    </>
  )
}

export default ClubDetailPage
